import React, { useState } from "react";
import PropTypes from "prop-types";

import { Button } from "components/core/Button";
import { Paragraph } from "components/core/Typography";
import { Heading } from "components/storages/Common";
import { StarRating } from "components/review/StarRating";
import Select from "components/core/Select/Select";
import ReviewCard from "./ReviewCard";
import "./ReviewsList.scss";

// SORTING ALGOS
export const sortByHighestRated = (reviews) =>
	reviews.sort((a, b) => b.rating - a.rating);
export const sortByLowestRated = (reviews) =>
	reviews.sort((a, b) => a.rating - b.rating);
export const sortByNewest = (reviews) =>
	reviews.sort((a, b) => b.dateNum - a.dateNum);
export const sortByOldest = (reviews) =>
	reviews.sort((a, b) => a.dateNum - b.dateNum);

// OPTIONS MAP
const sortOptions = {
	highestRated: {
		label: "Highest Rated",
		value: "highestRated",
		sortingAlgorithm: sortByHighestRated
	},
	lowestRated: {
		label: "Lowest Rated",
		value: "lowestRated",
		sortingAlgorithm: sortByLowestRated
	},
	newest: {
		label: "Newest",
		value: "newest",
		sortingAlgorithm: sortByNewest
	},
	oldest: { label: "Oldest", value: "oldest", sortingAlgorithm: sortByOldest }
};

const ReviewsList = ({
	totalReviews = 0,
	averageRating = 0,
	reviews = [],
	defaultSort = "highestRated",
	reviewsPerPage = 5,
	hideHeading = false
}) => {
	const [sortType, setSortType] = useState(defaultSort);
	const [pageCount, setPageCount] = useState(0);
	const MAX_PAGES = Math.ceil(totalReviews / reviewsPerPage);
	const canLoadMore = pageCount < MAX_PAGES - 1;

	const handleChange = (e) => {
		setSortType(e.target.value);
	};

	return (
		<div className="facility-reviews">
			<div className="facility-reviews--header">
				<Paragraph
					size="small"
					color="gray"
					className="reviews-description"
				>
					Reviews originally collected from the SpareFoot Marketplace.
				</Paragraph>
			</div>
			<div className="facility-reviews--subheader">
				<div>
					<StarRating
						outOf={5}
						rating={averageRating}
						withLabel
					/>
					{hideHeading ? (
						<span className="sf-type sf-heading size-standard color-gray weight-bold total-reviews-text">
							{`${totalReviews} Total Reviews`}
						</span>
					) : (
						<Heading
							color="gray"
							content={`${totalReviews} Total Reviews`}
							level="3"
							size="standard"
						/>
					)}
				</div>
				<Select
					className="reviews-sort"
					id="sort-select"
					label="Sort By:"
					options={Object.values(sortOptions)}
					onChange={handleChange}
					defaultValue={defaultSort}
				/>
			</div>

			<div className="review-cards">
				{sortOptions[sortType]
					.sortingAlgorithm(reviews)
					.slice(0, reviewsPerPage * (pageCount + 1))
					.map((review, i) => (
						<ReviewCard
							key={review.id ?? i}
							{...review}
							hideHeading={hideHeading}
						/>
					))}
			</div>
			{canLoadMore && (
				<Button
					theme="primary"
					onClick={() => setPageCount(pageCount + 1)}
					className="load-more-reviews-button"
				>
					Load More Reviews
				</Button>
			)}
		</div>
	);
};

ReviewsList.propTypes = {
	reviews: PropTypes.arrayOf(PropTypes.shape(ReviewCard.propTypes)),
	totalReviews: PropTypes.number,
	averageRating: PropTypes.number,
	defaultSort: PropTypes.string,
	reviewsPerPage: PropTypes.number,
	hideHeading: PropTypes.bool
};

export default ReviewsList;
